import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SOCIAL_WALLET_TYPE, useSocialWallet } from '@rango-dev/internal-social-wallet';
import { WalletState } from '@rango-dev/ui';
import { Box, Flex, Grid, Separator, Text, TextField } from '@rango-dev/whale';
import { useWalletList, useWallets, } from '@rango-dev/widget-embedded';
import { Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_WALLET_INJECTION_ERROR } from '../../constants/errors';
import { GTMEvents } from '../../constants/events';
import { STATEFUL_CONNECT_MODAL_ID } from '../../constants/modals';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useStackModal } from '../../hooks/useStackModal';
import { useStatefulConnect } from '../../hooks/useStatefulConnect';
import { walletAndSupportedChainsNames } from '../../utils/wallets';
import { SocialWalletIntroModal } from '../SocialWalletIntroModal';
import { WalletItem } from '../WalletItem';
import { ConnectWalletFooter } from './ConnectWalletFooter';
import { SOCIAL_WALLET_INTRO_CONFIRMED } from './ConnectWalletModal.constants';
import { getSocialWalletImage } from './ConnectWalletModal.helpers';
import { EmptyWalletList } from './EmptyWalletList';
import StatefulConnectContainer from './StatefulConnectContainer';
import { WalletsChainSelect } from './WalletsChainSelect';
const AUTO_HIDE_WALLET_ERROR_DURATION = 10_000;
// Height of the soft fade drawn at each scrollable edge of the wallet list.
const WALLETS_FADE_SIZE = '24px';
// Reset so the social banner is a real (keyboard-accessible) button, not an image.
const SOCIAL_BANNER_BUTTON_STYLE = {
    display: 'block',
    width: '100%',
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
};
/*
 * Full-width, but the intrinsic width/height attributes on the <img> reserve the
 * aspect ratio up front so the banner doesn't flicker/shift the modal on load.
 */
const SOCIAL_BANNER_IMG_STYLE = { display: 'block', width: '100%', height: 'auto' };
// Intrinsic SVG sizes (both 122px tall); used only to reserve the aspect ratio.
const SOCIAL_BANNER_WIDTH_DARK = 402;
const SOCIAL_BANNER_WIDTH_LIGHT = 350;
const SOCIAL_BANNER_HEIGHT = 122;
const ConnectWalletModal = (props) => {
    const { pop, push, stack } = useStackModal();
    const { t } = useTranslation();
    const { getState, handleConnect, resetState } = useStatefulConnect();
    const [socialWalletIntroConfirmed, setSocialWalletIntroConfirmed] = useLocalStorage(SOCIAL_WALLET_INTRO_CONFIRMED, false);
    const [searchValue, setSearchValue] = useState('');
    const [selectedChain, setSelectedChain] = useState(null);
    const [isWalletChainSelectOpen, setIsWalletChainSelectOpen] = useState(false);
    const [selectedWallet, setSelectedWallet] = useState(null);
    const [errors, setErrors] = useState([]);
    const { list } = useWalletList({});
    const { getWalletInfo, state, disconnect } = useWallets();
    const { getSocialUser } = useSocialWallet();
    const [showSocialWalletIntroModal, setShowSocialWalletIntroModal] = useState(false);
    const toggleSocialWalletIntroModal = () => setShowSocialWalletIntroModal((prevState) => !prevState);
    /*
     * Fade the wallet list at whichever edge has more content off-screen: a top
     * fade appears once scrolled down, a bottom fade while there is more below.
     * Toggled from the scroll position so a fully-visible list stays crisp.
     */
    const walletsScrollRef = useRef(null);
    const [scrollFade, setScrollFade] = useState({ top: false, bottom: false });
    const updateScrollFade = useCallback(() => {
        const element = walletsScrollRef.current;
        if (!element) {
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } = element;
        setScrollFade({
            top: scrollTop > 1,
            bottom: scrollTop + clientHeight < scrollHeight - 1,
        });
    }, []);
    const { indexInStack = 0, title, afterConnect, onDetachedConfirm, detachedConfirmText, forceConnectToNamespaces, renderNotification, renderHeader, renderModal, isMobile, isDarkMode, allBlockchains, options, shouldExcludeWallet, } = props;
    const lastStackItemId = stack[stack.length - 1]?.id;
    const showSocialWalletBanner = !options?.hideSocialWalletPromotionBanner &&
        list.some((wallet) => wallet.type === SOCIAL_WALLET_TYPE && wallet.state === WalletState.DISCONNECTED);
    const handleCloseMessages = (removeAll, message) => {
        const updatedMessages = errors.map((error) => {
            if (error.message === message || removeAll) {
                return { ...error, open: false };
            }
            return error;
        });
        setErrors(updatedMessages);
    };
    const catchErrorsWhileConnect = (error, wallet) => {
        let message = error instanceof Error ? error.message : error;
        message ||= !!error ? error.toString() : error;
        message ||= DEFAULT_WALLET_INJECTION_ERROR;
        // Handling various wallet error events for Google Tag Manager
        if (message && wallet) {
            // TODO: Properly type window to have `data layer`
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            window.dataLayer = window.dataLayer || [];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            window.dataLayer.push({
                event: GTMEvents.WALLET_FAILED,
                type: wallet.type,
                message,
            });
        }
        setErrors((prev) => [...prev, { open: true, message }]);
    };
    const displayedWallets = list
        .filter((wallet) => {
        const walletInfo = getWalletInfo(wallet.type);
        const supportedChainNames = walletAndSupportedChainsNames(walletInfo.supportedChains);
        // Drop wallet if it should be excluded
        if (!!shouldExcludeWallet && shouldExcludeWallet(walletInfo)) {
            return false;
        }
        // Drop wallet if its title does not contain the search value
        const isWalletSearched = wallet.title.toLowerCase().includes(searchValue.toLowerCase());
        if (!isWalletSearched) {
            return false;
        }
        // Drop wallet if it does not support the selected blockchain
        const walletSupportsSelectedBlockchain = !selectedChain || supportedChainNames?.includes(selectedChain);
        if (!walletSupportsSelectedBlockchain) {
            return false;
        }
        return true;
    })
        .map((wallet) => {
        /**
         * We want to display a different image for a social wallet
         * based on which social network (google, twitter, …) the user connected with.
         */
        if (wallet.type !== SOCIAL_WALLET_TYPE) {
            return wallet;
        }
        if (wallet.state !== WalletState.CONNECTED) {
            return wallet;
        }
        return {
            ...wallet,
            image: getSocialWalletImage(getSocialUser()),
        };
    });
    const theme = isDarkMode ? 'dark' : 'light';
    const emptyListImage = list.length === 0
        ? `/imgs/illustrations/empty-${theme}.svg`
        : `/imgs/illustrations/connect-wallets-modal-search-empty-${theme}.svg`;
    const handleSocialWalletConnect = () => {
        const showSocialWalletIntroModal = !options?.skipSocialWalletIntroModal && !socialWalletIntroConfirmed;
        if (showSocialWalletIntroModal) {
            toggleSocialWalletIntroModal();
            return;
        }
        connectToSocialWallet();
    };
    const handleWalletItemClick = (wallet) => {
        if (wallet.type !== SOCIAL_WALLET_TYPE) {
            resetState();
            setSelectedWallet(wallet);
            return;
        }
        const isConnected = state(wallet.type).connected;
        if (isConnected) {
            void disconnect(wallet.type);
            return;
        }
        handleSocialWalletConnect();
    };
    const connectToSocialWallet = () => {
        const socialWalletInfo = displayedWallets.find((wallet) => wallet.type === SOCIAL_WALLET_TYPE);
        if (socialWalletInfo) {
            void handleConnect(socialWalletInfo, {
                disconnectIfConnected: true,
                forceConnectToNamespaces: ['EVM', 'Solana'],
            });
        }
    };
    const onConfirmSocialWalletIntroModal = () => {
        setSocialWalletIntroConfirmed(true);
        setShowSocialWalletIntroModal(false);
        connectToSocialWallet();
    };
    useEffect(() => {
        const state = getState();
        if (state?.status !== 'init' &&
            selectedWallet &&
            lastStackItemId !== STATEFUL_CONNECT_MODAL_ID) {
            push({
                id: STATEFUL_CONNECT_MODAL_ID,
                renderContent: (index) => (_jsx(Box, { height: "100%", children: _jsx(StatefulConnectContainer, { selectedWallet: selectedWallet, catchErrorsWhileConnect: catchErrorsWhileConnect, indexInStack: index, onClose: () => {
                            setSelectedWallet(null);
                        }, detachedConfirmText: detachedConfirmText, onDetachedConfirm: () => {
                            if (onDetachedConfirm) {
                                onDetachedConfirm();
                            }
                            else {
                                resetState();
                                setSelectedWallet(null);
                            }
                        }, renderHeader: renderHeader, forceConnectToNamespaces: forceConnectToNamespaces }) })),
            });
        }
    }, [getState()]);
    useEffect(() => {
        if (lastStackItemId === STATEFUL_CONNECT_MODAL_ID &&
            (!selectedWallet || getState().status === 'init')) {
            pop();
        }
    }, [getState(), selectedWallet, lastStackItemId]);
    useEffect(() => {
        if (selectedWallet) {
            handleConnect(selectedWallet, {
                disconnectIfConnected: true,
                forceConnectToNamespaces,
            })
                .then((result) => {
                if (result.status === 'connected') {
                    afterConnect?.();
                }
            })
                .catch((e) => {
                catchErrorsWhileConnect(e, selectedWallet);
            });
        }
    }, [selectedWallet]);
    // Re-evaluate the fades whenever the rendered list can change its height.
    useEffect(() => {
        updateScrollFade();
    }, [displayedWallets.length, updateScrollFade]);
    const walletsFadeMask = `linear-gradient(to bottom, ${scrollFade.top ? 'transparent' : '#000'} 0, #000 ${WALLETS_FADE_SIZE}, #000 calc(100% - ${WALLETS_FADE_SIZE}), ${scrollFade.bottom ? 'transparent' : '#000'} 100%)`;
    const walletsScrollStyle = {
        maskImage: walletsFadeMask,
        WebkitMaskImage: walletsFadeMask,
    };
    return (_jsxs(Flex, { direction: "column", height: "100%", children: [_jsx(SocialWalletIntroModal, { isDarkMode: isDarkMode, open: showSocialWalletIntroModal, onClose: toggleSocialWalletIntroModal, onConfirm: onConfirmSocialWalletIntroModal }), renderHeader({
                showBackButton: indexInStack > 0,
                title: title ?? t('Connect Wallet'),
                handleClose: () => {
                    pop();
                },
            }), _jsxs(Flex, { direction: "column", flexGrow: "1", minHeight: "0", p: "4", children: [showSocialWalletBanner && (_jsxs(Flex, { direction: "column", gap: "5", pb: "4", children: [_jsx(Text, { size: "2", color: "gray", align: "left", children: t('Create a wallet quickly and easily using your social media accounts for a fast and seamless experience.') }), _jsx("button", { id: "app-connect-wallet-modal-social-banner-btn", type: "button", onClick: handleSocialWalletConnect, style: SOCIAL_BANNER_BUTTON_STYLE, children: _jsx("img", { src: `/imgs/wallets/social-wallet-banner-${isDarkMode ? 'dark' : 'light'}.svg`, alt: t('Connect via Social account'), width: isDarkMode ? SOCIAL_BANNER_WIDTH_DARK : SOCIAL_BANNER_WIDTH_LIGHT, height: SOCIAL_BANNER_HEIGHT, style: SOCIAL_BANNER_IMG_STYLE }) }), _jsxs(Flex, { align: "center", gap: "3", children: [_jsx(Text, { size: "1", color: "gray", wrap: "nowrap", children: t('All Wallets') }), _jsx(Separator, { size: "4" })] })] })), _jsxs(Flex, { gap: "2", pb: "5", align: "center", children: [_jsx(Box, { flexGrow: "1", children: _jsx(TextField.Root, { size: "3", variant: "surface", id: "app-connect-wallet-modal-search-wallet-input", value: searchValue, onChange: (event) => setSearchValue(event.target.value), placeholder: t('Search'), children: _jsx(TextField.Slot, { children: _jsx(Search, { size: 16, "aria-hidden": true }) }) }) }), !forceConnectToNamespaces?.length && (_jsx(WalletsChainSelect, { isOpen: isWalletChainSelectOpen, setIsOpen: setIsWalletChainSelectOpen, selectedChain: selectedChain, handleSelectedChainChange: (value) => setSelectedChain(value), shouldExcludeWallet: shouldExcludeWallet, isMobile: isMobile, renderModal: renderModal, allBlockchains: allBlockchains }))] }), errors.map((error) => renderNotification({
                        message: error,
                        setOpen: () => handleCloseMessages(false, error.message),
                        autoHideDuration: AUTO_HIDE_WALLET_ERROR_DURATION,
                    })), _jsx(Box, { ref: walletsScrollRef, flexGrow: "1", minHeight: "0", overflowY: "auto", onScroll: updateScrollFade, style: walletsScrollStyle, children: displayedWallets.length > 0 ? (_jsx(Grid, { columns: isMobile ? '2' : '3', gap: "2", children: displayedWallets.map((wallet) => (_jsx(WalletItem, { wallet: wallet, handleClick: handleWalletItemClick, disabled: false, disablePartialState: !!forceConnectToNamespaces?.length }, wallet.type))) })) : (_jsx(EmptyWalletList, { image: emptyListImage })) })] }), _jsx(Box, { flexShrink: "0", px: "4", pb: "4", children: _jsx(ConnectWalletFooter, {}) })] }));
};
export default ConnectWalletModal;
